import { Download, History } from 'lucide-react'
import { Link } from 'react-router-dom'

import {
  Avatar,
  Button,
  Card,
  DataTable,
  EmptyState,
  FilterBar,
  FilterPills,
  LoadingSkeleton,
  PageHeader,
  QueryBoundary,
  RelativeTime,
  SearchInput,
  Section,
  StatTile,
  StatusBadge,
  useSearchTerm,
  useToast,
  useUrlFilter,
  type Column,
} from '../../../components'
import { downloadCsv } from '../../../lib/csv'
import { todayIso } from '../../../lib/format'
import { t } from '../../../lib/i18n'
import { routes } from '../../../lib/routes'
import { useAccessLog, type AccessLogEntry } from '../../../fixtures'

/**
 * P-10 — Access log.
 *
 * THE RULE OF THIS SCREEN: there is no edit and no delete affordance anywhere
 * on it, for anybody, the platform owner included. No row action column, no
 * pencil, no trash, no restore, no context menu, no inline editing, and no row
 * click that could be mistaken for one. The immutability IS the feature — this
 * table is what settles the argument on the day a trader disputes a figure, and
 * a log its keeper can quietly amend settles nothing. The screen says so out
 * loud, under the table, rather than leaving the reader to infer it.
 *
 * Export is allowed: reading a log does not change it. It adds no control to
 * a row and no way to alter one — it writes the rows already on screen.
 *
 * CONTRACT GAP: there is no access-log export endpoint. P10 is a read and
 * nothing more. So the file is written in the browser, from the FILTERED rows
 * the reader is looking at, and the toast says exactly that rather than
 * implying the server prepared a complete export of the platform's log.
 */

type LogFilter = 'all' | 'edited' | 'no-ticket'

const LOG_FILTERS: readonly LogFilter[] = ['all', 'edited', 'no-ticket']

export function AccessLogScreen() {
  const log = useAccessLog()
  const toast = useToast()
  const [filter, setFilter] = useUrlFilter<LogFilter>('show', 'all', LOG_FILTERS)
  const term = useSearchTerm()

  const entries = log.data?.entries ?? []

  // Filtered once, here, so the file and the table can never disagree about
  // which rows the reader is looking at.
  const rows = entries.filter((entry) => {
    if (filter === 'edited' && !entry.edited) return false
    if (filter === 'no-ticket' && entry.ticket !== null) return false
    if (!term) return true
    return (
      entry.business.toLowerCase().includes(term) || entry.person.toLowerCase().includes(term)
    )
  })

  const exportCsv = () => {
    if (rows.length === 0) {
      toast.show(t('p.log.exportEmpty'), 'info')
      return
    }

    downloadCsv(
      `${t('p.log.exportFilename')}-${todayIso()}`,
      [
        t('label.when'),
        t('label.who'),
        t('label.role'),
        t('p.log.colWhoseBook'),
        t('label.why'),
        t('p.log.colTicket'),
        t('label.how'),
        t('label.for'),
      ],
      rows.map((entry) => [
        `${entry.at} ${entry.time}`,
        entry.person,
        t(`platformRole.${entry.role}`),
        entry.business,
        entry.reason,
        // No ticket is an EMPTY cell, not the word "none" — the gap is what the
        // row is being read for.
        entry.ticket ?? '',
        entry.edited ? t('p.log.edited') : t('p.log.readOnly'),
        entry.minutes,
      ]),
    )

    toast.show(t('p.log.exported', { count: rows.length }))
  }

  const columns: Column<AccessLogEntry>[] = [
    {
      id: 'when',
      header: t('label.when'),
      width: '17%',
      sortBy: (entry) => `${entry.at} ${entry.time}`,
      cell: (entry) => (
        <RelativeTime date={entry.at} time={entry.time} className="whitespace-nowrap tnum" />
      ),
    },
    {
      id: 'who',
      header: t('label.who'),
      sortBy: (entry) => entry.person,
      cell: (entry) => (
        <div className="flex items-center gap-2.5">
          <Avatar name={entry.person} size="sm" />
          <div className="min-w-0">
            <div className="font-semibold text-dark">{entry.person}</div>
            <div className="text-[11.5px] text-muted">{t(`platformRole.${entry.role}`)}</div>
          </div>
        </div>
      ),
    },
    {
      id: 'business',
      header: t('p.log.colWhoseBook'),
      sortBy: (entry) => entry.business,
      cell: (entry) => (
        <Link
          to={routes.platform.business(entry.slug)}
          className="font-semibold text-accent-dark underline-offset-2 hover:underline"
        >
          {entry.business}
        </Link>
      ),
    },
    {
      id: 'why',
      header: t('label.why'),
      cell: (entry) => (
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
          <span>{entry.reason}</span>
          {entry.ticket === null ? (
            <StatusBadge tone="warn">{t('p.log.filterNoTicket')}</StatusBadge>
          ) : (
            <span className="tnum text-muted">
              {t('p.log.ticketRef', { number: entry.ticket })}
            </span>
          )}
        </div>
      ),
    },
    {
      id: 'how',
      header: t('label.how'),
      cell: (entry) =>
        entry.edited ? (
          <StatusBadge tone="warn">{t('p.log.edited')}</StatusBadge>
        ) : (
          <StatusBadge tone="neutral">{t('p.log.readOnly')}</StatusBadge>
        ),
    },
    {
      id: 'for',
      header: t('label.for'),
      numeric: true,
      sortBy: (entry) => entry.minutes,
      cell: (entry) => (
        <span className="font-display font-bold whitespace-nowrap tnum">
          {entry.minutes} {t('unit.minutes')}
        </span>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title={t('p.log.title')}
        subtitle={t('p.log.subtitle')}
        actions={
          <Button icon={<Download className="size-4" />} onClick={exportCsv}>
            {t('act.export')}
          </Button>
        }
      />

      <QueryBoundary
        query={log}
        skeleton={
          <div className="flex flex-col gap-3.5">
            <LoadingSkeleton variant="tiles" />
            <Card surface="heavy">
              <LoadingSkeleton variant="table" rows={5} />
            </Card>
          </div>
        }
        isEmpty={(data) => data.entries.length === 0}
        empty={
          <EmptyState
            icon={<History className="size-6" />}
            title={t('p.log.emptyTitle')}
            body={t('p.log.emptyBody')}
          />
        }
      >
        {(data) => {
          return (
            <>
              <div className="mb-4 grid gap-3.5 sm:grid-cols-2 xl:grid-cols-4">
                <StatTile
                  label={t('p.log.booksOpened')}
                  value={data.booksOpened}
                  sub={t('p.log.booksOpenedSub')}
                />
                <StatTile
                  label={t('p.log.anythingEdited')}
                  value={data.editedCount}
                  sub={t('p.log.anythingEditedSub')}
                />
                <StatTile
                  label={t('p.log.averageVisit')}
                  value={
                    <>
                      {data.averageMinutes}
                      <span className="ms-1 text-[15px] font-bold text-muted">
                        {t('unit.minutes')}
                      </span>
                    </>
                  }
                  sub={t('p.log.averageVisitSub')}
                />
                <StatTile
                  label={t('p.log.ownersNotified')}
                  value={
                    <span className="text-success">
                      {t('p.log.ownersNotifiedValue', {
                        done: data.ownersNotified,
                        total: data.booksOpened,
                      })}
                    </span>
                  }
                  sub={t('p.log.ownersNotifiedSub')}
                />
              </div>

              <FilterBar>
                <SearchInput placeholder={t('p.log.searchPlaceholder')} />
                <FilterPills<LogFilter>
                  value={filter}
                  onChange={setFilter}
                  pills={[
                    { value: 'all', label: t('p.log.filterEverything') },
                    { value: 'edited', label: t('p.log.filterEdited') },
                    { value: 'no-ticket', label: t('p.log.filterNoTicket') },
                  ]}
                />
              </FilterBar>

              <Section>
                {/*
                  No `rowActions`, no `onRowClick`. Deliberate: this table is a
                  receipt, not a workspace. The only interactive thing in a row
                  is the link to the business whose book was opened.
                */}
                <DataTable
                  columns={columns}
                  rows={rows}
                  rowKey={(entry) => entry.id}
                  caption={t('p.log.title')}
                  empty={
                    <EmptyState
                      icon={<History className="size-6" />}
                      title={t('table.noResults')}
                      body={t('p.log.emptyBody')}
                    />
                  }
                />
                <p className="mt-3.5 border-t border-muted/15 pt-3.5 text-xs leading-relaxed text-muted">
                  <b className="font-bold text-sub">{t('p.log.immutable')}</b>{' '}
                  {t('p.log.immutableWhy')}
                </p>
              </Section>

              {/*
                Shown only when there IS such a row. The panel used to name a
                business and a date out of the design deck — "the Rana Mandi
                visit on 24 August" — beside a table of real visits, and it said
                it whether or not any row was missing a ticket.
              */}
              {data.entries.some((entry) => entry.ticket === null) ? (
                <Section surface="accent" title={t('p.log.noTicketTitle')} className="mt-4">
                  <p className="text-[12.5px] leading-relaxed text-sub">
                    {t('p.log.noTicketBody')}
                  </p>
                </Section>
              ) : null}
            </>
          )
        }}
      </QueryBoundary>
    </>
  )
}
